/**
 * Utilitaires pour la gestion des images
 */

import { getFirebaseStorage } from '@/shared/services/firebase/config';
import { ref, getDownloadURL, uploadBytes } from 'firebase/storage';
import { logger } from './logger';

/**
 * Construit l'URL d'une image depuis Firebase Storage
 * Accepte les URLs gs:// ou les chemins relatifs
 */
export const getFirebaseImageUrl = async (path: string): Promise<string> => {
  const storage = getFirebaseStorage();
  if (!storage) {
    throw new Error('Firebase Storage n\'est pas initialisé');
  }

  // Si c'est une URL gs://, extraire le chemin
  let storagePath = path;
  if (path.startsWith('gs://')) {
    // Format: gs://bucket-name.app/path/to/file ou gs://bucket-name/path/to/file
    // Extraire tout ce qui suit le premier slash après le nom du bucket
    const match = path.match(/gs:\/\/[^/]+\.app\/(.+)/) || path.match(/gs:\/\/[^/]+\/(.+)/);
    if (match && match[1]) {
      storagePath = match[1];
    }
  }

  try {
    const storageRef = ref(storage, storagePath);
    return await getDownloadURL(storageRef);
  } catch (error) {
    logger.error('Erreur lors de la récupération de l\'URL:', error);
    throw error;
  }
};

/**
 * Construit l'URL d'une image depuis le dossier public
 */
export const getPublicImageUrl = (path: string): string => {
  // Enlever le leading slash si présent
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `/${cleanPath}`;
};

/**
 * Construit l'URL d'une image avec fallback
 * Pour les URLs gs://, retourne une Promise avec l'URL de téléchargement
 */
export const getImageUrl = async (
  url: string | null | undefined,
  fallback?: string
): Promise<string> => {
  if (!url) {
    return fallback || '';
  }

  // Si c'est déjà une URL complète (http/https)
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // Si c'est un chemin Firebase Storage (gs://), récupérer l'URL de téléchargement
  if (url.startsWith('gs://')) {
    return await getFirebaseImageUrl(url);
  }

  // Sinon, considérer comme chemin public
  return getPublicImageUrl(url);
};

/**
 * Upload une image vers Firebase Storage
 * @param file - Le fichier à uploader
 * @param path - Le chemin de destination dans Firebase Storage (ex: "museums/{museumId}/exhibitions/{exhibitionId}/cover.jpg")
 * @returns L'URL gs:// de l'image uploadée
 */
export const uploadImageToStorage = async (
  file: File,
  path: string
): Promise<string> => {
  const storage = getFirebaseStorage();
  if (!storage) {
    throw new Error('Firebase Storage n\'est pas initialisé');
  }

  try {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);

    // Retourner l'URL gs://
    const bucket = storage.app.options.storageBucket;
    return `gs://${bucket}/${path}`;
  } catch (error) {
    logger.error('Erreur lors de l\'upload de l\'image:', error);
    throw error;
  }
};

/**
 * Upload un fichier audio vers Firebase Storage
 * @param file - Le fichier audio à uploader
 * @param path - Le chemin de destination dans Firebase Storage (ex: "museums/{museumId}/exhibitions/{exhibitionId}/works/audio.mp3")
 * @returns L'URL gs:// du fichier audio uploadé
 */
export const uploadAudioToStorage = async (
  file: File,
  path: string
): Promise<string> => {
  const storage = getFirebaseStorage();
  if (!storage) {
    throw new Error('Firebase Storage n\'est pas initialisé');
  }

  try {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);

    // Retourner l'URL gs://
    const bucket = storage.app.options.storageBucket;
    return `gs://${bucket}/${path}`;
  } catch (error) {
    logger.error('Erreur lors de l\'upload de l\'audio:', error);
    throw error;
  }
};

